import { openEvolutionModal, responseData } from 'Pages/AnalyzersV2/@signals'

function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findByTooltip(text) {
	const els = document.querySelectorAll('[aria-label], [title]')
	for (const el of els) {
		const label = (el.getAttribute('aria-label') || el.getAttribute('title') || '').toLowerCase()
		if (label.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function hasRealData() {
	return Object.keys(responseData.value || {}).length > 0
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	// 0 — Intro
	{
		type: 'intro',
		icon: 'star',
		title: ES ? 'Bienvenido al Analizador de Rating' : 'Welcome to the Rating Analyzer',
		description: ES
			? 'El Analizador de Rating evalúa las puntuaciones y valoraciones de clientes en distintos proveedores y plataformas de reseñas. Entiende cómo se compara la calidad de tu servicio frente a los competidores.'
			: 'The Rating Analyzer evaluates customer review scores and ratings across different providers and review platforms. Understand how your service quality compares to competitors.',
	},
	// 1 — Report Selector
	{
		type: 'field',
		skipIf: () => !document.querySelector('input[placeholder="Please, select a report"]'),
		findTarget: () => {
			const input = document.querySelector('input[placeholder="Please, select a report"]')
			return input?.closest('.MuiAutocomplete-root') || input?.closest('.MuiFormControl-root')
		},
		icon: 'file-lines',
		title: ES ? 'Selector de Informe' : 'Report Selector',
		description: ES
			? 'Selecciona una ejecución de informe de este desplegable para cargar los datos del análisis de rating. Los informes de rating agregan puntuaciones de reseñas de múltiples fuentes.'
			: 'Select a report execution from this dropdown to load the rating analysis data. Rating reports aggregate review scores from multiple sources.',
	},
	// 2 — Rating Table
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Analyzer: Price Table')
			return el?.closest('.MuiCard-root') || el?.closest('.MuiGrid-item')
		},
		icon: 'table-cells',
		title: ES ? 'Tabla de Ratings' : 'Rating Table',
		description: ES
			? 'La tabla comparativa principal que muestra las puntuaciones de rating por proveedor. Las celdas resaltadas en verde indican la puntuación más alta de la columna y en rojo la más baja.'
			: 'The main comparison table showing rating scores by provider. Green-highlighted cells indicate the highest score in the column, red the lowest.',
		example: ES ? 'Verde = Puntuación más alta | Rojo = Puntuación más baja' : 'Green = Highest Rating | Red = Lowest Rating',
	},
	// 3 — Toggle Details
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Hide Details')
			return el?.parentElement
		},
		icon: 'toggle-on',
		title: ES ? 'Mostrar/Ocultar Detalles' : 'Toggle Details',
		description: ES
			? 'Muestra u oculta información adicional en cada celda: número de reseñas, desglose por categoría y plataforma de origen. Con detalles ocultos la tabla es más compacta.'
			: 'Show or hide additional information in each cell: review count, category breakdown, and source platform. Hiding details makes the table more compact.',
	},
	// 4 — Rating Cell → opens evolution DIRECTLY (no popover in Rating)
	// action: demoMode si no hay datos, click en celda real si los hay
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-price-cell"]') ||
			(() => {
				const rows = document.querySelectorAll('table tbody tr')
				for (const row of rows) {
					const cells = row.querySelectorAll('td')
					if (cells.length > 1) return cells[1]
				}
				return null
			})()
		),
		action: () => {
			if (!hasRealData()) {
				openEvolutionModal.value = { demoMode: true }
			} else {
				const rows = document.querySelectorAll('table tbody tr')
				for (const row of rows) {
					const cells = row.querySelectorAll('td')
					if (cells.length > 1) {
						// La celda en Rating tiene un Box con onClick que abre evolution directamente
						const box = cells[1].querySelector('.MuiBox-root')
						if (box) { box.click(); break }
					}
				}
			}
		},
		waitFor: () => !!document.querySelector('.MuiDialog-paperFullScreen'),
		icon: 'star',
		title: ES ? 'Clic en Celda → Evolución Directa' : 'Cell Click → Direct Evolution',
		description: ES
			? 'A diferencia de otros analizadores, en Rating no hay popover intermedio: hacer clic en cualquier celda abre directamente el gráfico de evolución histórica de esa puntuación. Más rápido y directo.'
			: 'Unlike other analyzers, Rating has no intermediate popover: clicking any cell directly opens the historical evolution chart for that score. Faster and more direct.',
	},
	// 5 — Evolution Chart overview (modal OPEN)
	// action: cierra el modal al pasar al siguiente paso
	// cleanup: safety net si el tour se cierra mientras está en este paso
	{
		type: 'field',
		findTarget: () => document.querySelector('.MuiDialog-paperFullScreen'),
		action: () => {
			openEvolutionModal.value = {}
		},
		cleanup: () => {
			openEvolutionModal.value = {}
		},
		icon: 'chart-line',
		title: ES ? 'Gráfico de Evolución de Rating' : 'Rating Evolution Chart',
		description: ES
			? 'Vista completa de la evolución de las puntuaciones a lo largo del tiempo. Muestra las tendencias de valoración para todos los proveedores seleccionados, con una línea por proveedor.'
			: 'Full view of rating score evolution over time. Shows rating trends for all selected providers, with one line per provider.',
	},
	// 6 — Rating Chart (isRating muestra Sites Autocomplete + Price values/Variation toggle)
	{
		type: 'field',
		findTarget: () => {
			const els = document.querySelectorAll('.MuiToggleButtonGroup-root')
			for (const el of els) {
				const text = el.textContent.toLowerCase()
				if (text.includes('price values') || text.includes('variation')) return el
			}
			return null
		},
		icon: 'chart-bar',
		title: ES ? 'Gráfico de Ratings' : 'Rating Chart',
		description: ES
			? 'Comparación visual de ratings entre proveedores. El filtro de plataforma (Sites) permite aislar las puntuaciones de una fuente concreta (Google Reviews, Trustpilot, etc.). Alterna entre valores absolutos y variación porcentual.'
			: 'Visual comparison of ratings across providers. The site filter lets you isolate scores from a specific review source (Google Reviews, Trustpilot, etc.). Toggle between absolute values and percentage variation.',
		example: ES ? 'Price values = Puntuación | Variation = % de diferencia' : 'Price values = Score | Variation = % difference',
	},
	// 7 — Fullscreen Chart
	{
		type: 'field',
		findTarget: () => findByTooltip('Fullscreen'),
		icon: 'expand',
		title: ES ? 'Gráfico en Pantalla Completa' : 'Fullscreen Chart',
		description: ES
			? 'Amplía el gráfico a pantalla completa para una vista más clara. Especialmente útil al comparar muchos proveedores o cuando quieres analizar la tendencia de rating con más detalle.'
			: 'Expand the chart to full screen for a clearer view. Especially useful when comparing many providers or when you want to analyze the rating trend in more detail.',
	},
	// 8 — Outro
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Haz clic en cualquier celda de rating para ver su evolución histórica directamente. Usa el filtro de plataforma en el gráfico para comparar puntuaciones por fuente de reseñas.'
			: 'Click on any rating cell to directly view its historical evolution. Use the site filter on the chart to compare scores by review source.',
	},
]
